import type { ReactNode } from "react";
import { IconChevronRight } from "./icons";

type Tone = "default" | "orange" | "blue" | "green";

const badgeToneClasses: Record<Tone, string> = {
  default: "bg-[var(--surface-2)] text-[var(--text-2)]",
  orange: "bg-[var(--sf-primary)] text-white",
  blue: "bg-sky-500 text-white",
  green: "bg-emerald-500 text-white",
};

export function Card({
  title,
  icon,
  badge,
  badgeTone = "orange",
  onClick,
  children,
  className = "",
  titleSize = "md",
  multilineTitle = false,
}: {
  title: ReactNode;
  icon?: ReactNode;
  badge?: number | string;
  badgeTone?: Tone;
  onClick?: () => void;
  children?: ReactNode;
  className?: string;
  titleSize?: "sm" | "md";
  multilineTitle?: boolean;
}) {
  const interactive = Boolean(onClick);
  const Wrapper = interactive ? "button" : "div";
  const titleClass =
    titleSize === "sm"
      ? "text-[17px] sm:text-[19px]"
      : "text-[21px] sm:text-[22px]";
  return (
    <Wrapper
      {...(interactive ? { type: "button" as const, onClick } : {})}
      className={`block w-full min-w-0 rounded-3xl bg-[var(--surface-1)] p-4 text-left shadow-[0_1px_3px_rgba(15,23,42,0.04),0_1px_2px_rgba(15,23,42,0.06)] ring-1 ring-[var(--border-1)] transition active:scale-[0.99] sm:p-5 ${interactive ? "cursor-pointer hover:brightness-[0.98] dark:hover:brightness-110" : ""} ${className}`}
    >
      <div className="flex min-w-0 items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          {icon ? (
            <span className="shrink-0 text-[var(--text-3)]">{icon}</span>
          ) : null}
          <span
            className={`min-w-0 font-extrabold tracking-tight text-[var(--text-1)] ${multilineTitle ? "leading-snug" : "truncate"} ${titleClass}`}
          >
            {title}
          </span>
          {badge !== undefined && badge !== 0 ? (
            <span
              className={`ml-1 inline-flex h-6 min-w-6 shrink-0 items-center justify-center rounded-full px-1.5 text-[12px] font-bold ${badgeToneClasses[badgeTone]}`}
            >
              {badge}
            </span>
          ) : null}
        </div>
        {interactive ? (
          <span
            aria-hidden
            className="inline-flex size-7 shrink-0 items-center justify-center rounded-full bg-[var(--surface-2)] text-[var(--text-3)] sm:size-8"
          >
            <IconChevronRight width={14} height={14} strokeWidth={2.5} />
          </span>
        ) : null}
      </div>
      {children ? <div className="mt-4 min-w-0">{children}</div> : null}
    </Wrapper>
  );
}
